import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import Scrollbars from "react-custom-scrollbars-2";
import { useWorkspaceAccounts } from "@api/account.api";
import { Account } from "@common/types/domains/accounting/account";
import { BaseFilterProps } from "../../lib";
import { FilterActionsFooter } from "../../../../general/Filter/FilterActionsFooter/FilterActionsFooter";
import { FilterSearch } from "../../../../general/FilterSearch/FilterSearch";
import { FormCheckbox } from "../../../../forms/FormCheckbox/FormCheckbox";
import styles from "./CoaAccountFilter.module.scss";

export const CoaAccountFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => {
    const [search, setSearch] = useState("");
    const searchRef = useRef<HTMLInputElement>();

    const { data: accounts } = useWorkspaceAccounts();

    useEffect(() => {
        searchRef.current?.focus();

        return () => {
            setSearch("");
        };
    }, []);

    const { displayedAccounts, displayedAccountsMap } = useMemo(() => {
        if (!accounts) {
            return {
                displayedAccounts: [],
                displayedAccountsMap: new Map<string, Account[]>(),
            };
        }

        const accountsByNameMap: Map<string, Account[]> = new Map();

        for (const account of accounts) {
            if (
                search &&
                !account.name.toLowerCase().includes(search.toLowerCase())
            ) {
                continue;
            }

            if (
                filters.entityIds &&
                !filters.entityIds.includes(account.entityId)
            ) {
                continue;
            }

            if (accountsByNameMap.has(account.name)) {
                accountsByNameMap.get(account.name)!.push(account);
            } else {
                accountsByNameMap.set(account.name, [account]);
            }
        }

        return {
            displayedAccounts: [...accountsByNameMap.values()].map(
                (accountsForName) => ({
                    name: accountsForName[0].name,
                    accounts: accountsForName,
                }),
            ),
            displayedAccountsMap: accountsByNameMap,
        };
    }, [accounts, search, filters.entityIds]);

    const handleChange = useCallback(
        (accountId: string) => {
            const accountsToChange = displayedAccountsMap.get(accountId);
            if (!accountsToChange) {
                return;
            }

            // when only some of them are selected, remove all of them?
            if (
                accountsToChange.some((account) =>
                    filters.coaAccounts?.includes(account.id),
                )
            ) {
                onChange({
                    coaAccounts: filters.coaAccounts?.filter(
                        (id) =>
                            !accountsToChange.some(
                                (account) => account.id === id,
                            ),
                    ),
                });
            } else {
                onChange({
                    coaAccounts: [
                        ...(filters.coaAccounts ?? []),
                        ...accountsToChange.map((account) => account.id),
                    ],
                });
            }
        },
        [filters.coaAccounts, onChange, displayedAccountsMap],
    );

    return (
        <div className={styles.filter}>
            <FilterSearch
                value={search}
                onChange={setSearch}
                inputRef={searchRef}
            />
            <div className={styles.content}>
                <Scrollbars style={{ width: "100%", height: "100%" }}>
                    {displayedAccounts.length > 0 ? (
                        <>
                            {displayedAccounts.map((item) => (
                                <FormCheckbox
                                    value={item.name}
                                    key={item.name}
                                    isChecked={item.accounts.some((account) =>
                                        filters.coaAccounts?.includes(
                                            account.id,
                                        ),
                                    )}
                                    handleChange={handleChange}
                                    className={styles.checkbox}
                                    small
                                    label={item.name}
                                />
                            ))}
                        </>
                    ) : (
                        <p className={styles.empty}>No account codes found</p>
                    )}
                </Scrollbars>
            </div>

            {!!filters.coaAccounts?.length && (
                <FilterActionsFooter
                    deselectFunction={() =>
                        onChange({ coaAccounts: undefined })
                    }
                    selectedCount={filters.coaAccounts.length}
                />
            )}
        </div>
    );
};
